import NotFound from '../NotFound';
import { CONTENT_PACK } from '../../data/contentPack';
import PackArticle from '../../components/seo/PackArticle';
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Plane, ExternalLink, FileText, Ruler } from 'lucide-react';
import { SEOLayout } from '../../components/SEOLayout';
import { IataRequirementsList } from '../../data/iataRules';
import { verifiedAirlineFacts } from '../../data/airlineVerified';
import { allAirlinesData as airlinesData } from '../../data/seoDataMerged';
import { Button } from '../../components/ui/button';

const AirlineDetail = () => {
  const { slug } = useParams();
  const packPage = CONTENT_PACK[`/airlines/${slug}`];
  const airline = airlinesData[slug];

  if (packPage) return <PackArticle page={packPage} />;
  if (!airline) return <NotFound />;

  const verified = verifiedAirlineFacts[slug];

  return (
    <SEOLayout
      title={airline.seoTitle}
      description={airline.seoDescription}
      keywords={airline.seoKeywords}
      breadcrumbs={[
        { label: 'Airlines', href: '/airlines' },
        { label: airline.name }
      ]}
    >
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center">
            <Plane className="w-8 h-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
              {airline.name} Pet Travel Policy
            </h1>
            <p className="text-slate-600">
              {airline.code} • {airline.country}{airline.alliance ? ` • ${airline.alliance}` : ''}
            </p>
          </div>
        </div>
        <p className="text-lg text-slate-700 max-w-3xl">
          Airline pet rules (cabin weight, bag size, minimum age, fees, breed restrictions) change often.
          We only show {airline.name} facts we have checked on the airline's official website, with the date of verification.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mb-12">
        <div className="lg:col-span-2 space-y-8">
          <section data-testid="airline-facts" className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {airline.name} cabin and cargo rules
            </h2>
            {verified ? (
              <div className="space-y-3">
                {verified.facts.map((fact) => (
                  <div key={fact.label} className="p-3 bg-slate-50 rounded-lg text-sm">
                    <span className="font-medium text-slate-900">{fact.label}: </span>
                    <span className="text-slate-700">{fact.value}</span>
                    <p className="text-xs text-slate-500 mt-1">
                      Source: <a href={fact.sourceUrl} target="_blank" rel="noopener noreferrer" className="underline">{fact.sourceUrl}</a> — Verified on {fact.verifiedOn}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div data-testid="airline-facts-unverified" className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                <p className="mb-3">
                  We have not yet verified {airline.name}'s pet rules on its official website, so we do not display them here.
                  See the official policy for cabin weight limits, carrier dimensions, minimum age, fees and breed restrictions.
                </p>
                <a href={airline.officialPolicyUrl} target="_blank" rel="noopener noreferrer"
                  data-testid="airline-official-policy-link"
                  className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-800 font-medium">
                  See the official {airline.name} pet policy <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Ruler className="w-5 h-5 text-amber-600" />
              IATA container requirements (cargo)
            </h2>
            <IataRequirementsList />
            <p className="text-xs text-slate-500 mt-3">
              Airlines may apply stricter rules on top of IATA. Always check {airline.name}'s official policy.
            </p>
            <div className="flex flex-wrap gap-4 mt-4 text-sm font-medium">
              <Link data-testid="airline-calculator-link" to="/guides/iata-crate-size-calculator" className="text-blue-600">Free IATA crate size calculator →</Link>
              <Link to="/guides/iata-crate-requirements-2026" className="text-blue-600">IATA crate requirements checklist →</Link>
            </div>
          </section>
        </div>

        <div className="space-y-6">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 text-white">
            <h3 className="text-xl font-bold mb-3">Check Your Crate</h3>
            <p className="text-blue-100 text-sm mb-4">
              Enter your pet's measurements and upload 3 photos. We apply the IATA size formula and check what is visible.
            </p>
            <Link to="/">
              <Button className="w-full bg-white text-blue-600 hover:bg-blue-50">
                Check My Crate - $39
              </Button>
            </Link>
            <p className="text-blue-100 text-xs mt-3">Independent service, not affiliated with IATA or {airline.name}.</p>
          </div>

          <div className="bg-slate-100 rounded-xl p-6">
            <h3 className="font-bold text-slate-900 mb-3">Official Policy</h3>
            <a
              href={airline.officialPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              {airline.name} Pet Policy
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </SEOLayout>
  );
};

export default AirlineDetail;
